import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api, { CompanyEvent } from '../services/api';
import { DashboardStats } from '../types';

const StatCard = ({
  title,
  value,
  icon,
  color,
  link,
}: {
  title: string;
  value: string;
  icon: React.ReactNode;
  color: string;
  link?: string;
}) => {
  const content = (
    <div className={`bg-gray-800 rounded-xl p-6 border-l-4 ${color}`}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-gray-400 text-sm font-medium">{title}</p>
          <p className="text-3xl font-bold text-white mt-2">{value}</p>
        </div>
        <div className="text-gray-500">{icon}</div>
      </div>
    </div>
  );

  if (link) {
    return (
      <Link to={link} className="block hover:opacity-90 transition-opacity">
        {content}
      </Link>
    );
  }

  return content;
};

export default function DashboardPage() {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [upcomingEvents, setUpcomingEvents] = useState<CompanyEvent[]>([]);

  useEffect(() => {
    loadStats();
    loadUpcomingEvents();
  }, []);

  const loadStats = async () => {
    try {
      const data = await api.getDashboardStats();
      setStats(data);
    } catch (error) {
      console.error('Failed to load dashboard stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadUpcomingEvents = async () => {
    try {
      const events = await api.getUpcomingEvents(5);
      setUpcomingEvents(Array.isArray(events) ? events : []);
    } catch (error) {
      console.error('Failed to load upcoming events:', error);
    }
  };

  const getEventColor = (eventType: string, customColor?: string | null): string => {
    if (customColor) return customColor;
    const colors: Record<string, string> = {
      holiday: '#dc2626',
      team_meeting: '#2563eb',
      training: '#7c3aed',
      production: '#ea580c',
      shipping: '#0891b2',
      announcement: '#ca8a04',
      other: '#6b7280',
    };
    return colors[eventType] || '#6b7280';
  };

  const formatEventDate = (dateStr: string, allDay: boolean): string => {
    const date = new Date(dateStr);
    const options: Intl.DateTimeFormatOptions = {
      weekday: 'short',
      month: 'short',
      day: 'numeric'
    };
    if (!allDay) {
      options.hour = '2-digit';
      options.minute = '2-digit';
    }
    return date.toLocaleDateString('en-US', options);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-white">Shipping Dashboard</h1>
        <p className="text-gray-400 mt-1">Overview of shipping operations</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
        <StatCard
          title="Ready to Ship"
          value={stats?.ready_to_ship || '0'}
          color="border-blue-500"
          link="/ready-to-ship"
          icon={
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17a2 2 0 11-4 0 2 2 0 014 0zM19 17a2 2 0 11-4 0 2 2 0 014 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16V6a1 1 0 00-1-1H4a1 1 0 00-1 1v10a1 1 0 001 1h1m8-1a1 1 0 01-1 1H9m4-1V8a1 1 0 011-1h2.586a1 1 0 01.707.293l3.414 3.414a1 1 0 01.293.707V16a1 1 0 01-1 1h-1m-6-1a1 1 0 001 1h1M5 17a2 2 0 104 0m-4 0a2 2 0 114 0m6 0a2 2 0 104 0m-4 0a2 2 0 114 0" />
            </svg>
          }
        />

        <StatCard
          title="Will Call Ready"
          value={stats?.will_call_ready || '0'}
          color="border-yellow-500"
          link="/will-call"
          icon={
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
            </svg>
          }
        />

        <StatCard
          title="Shipped Today"
          value={stats?.shipped_today || '0'}
          color="border-green-500"
          link="/tracking"
          icon={
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          }
        />

        <StatCard
          title="Pending Pickup"
          value={stats?.pending_pickup || '0'}
          color="border-orange-500"
          icon={
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          }
        />

        <StatCard
          title="Active Shipments"
          value={stats?.active_shipments || '0'}
          color="border-indigo-500"
          link="/shipments"
          icon={
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17a2 2 0 11-4 0 2 2 0 014 0zM19 17a2 2 0 11-4 0 2 2 0 014 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16V6a1 1 0 00-1-1H4a1 1 0 00-1 1v10a1 1 0 001 1h1m8-1a1 1 0 01-1 1H9m4-1V8a1 1 0 011-1h2.586a1 1 0 01.707.293l3.414 3.414a1 1 0 01.293.707V16a1 1 0 01-1 1h-1m-6-1a1 1 0 001 1h1M5 17a2 2 0 104 0m-4 0a2 2 0 114 0m6 0a2 2 0 104 0m-4 0a2 2 0 114 0" />
            </svg>
          }
        />

        <StatCard
          title="Delivered This Week"
          value={stats?.delivered_this_week || '0'}
          color="border-purple-500"
          icon={
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          }
        />
      </div>

      {/* Quick Actions */}
      <div className="bg-gray-800 rounded-xl p-6 border-l-4 border-l-green-500">
        <h2 className="text-lg font-semibold text-white mb-4">Quick Actions</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Link
            to="/ready-to-ship"
            className="flex items-center p-4 bg-gray-700 rounded-lg hover:bg-gray-600 transition-colors"
          >
            <div className="p-3 bg-blue-600 rounded-lg mr-4">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
            </div>
            <div>
              <p className="text-white font-medium">Process Queue</p>
              <p className="text-gray-400 text-sm">Book shipments and schedule pickups</p>
            </div>
          </Link>

          <Link
            to="/will-call"
            className="flex items-center p-4 bg-gray-700 rounded-lg hover:bg-gray-600 transition-colors"
          >
            <div className="p-3 bg-yellow-600 rounded-lg mr-4">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
            </div>
            <div>
              <p className="text-white font-medium">Will Call</p>
              <p className="text-gray-400 text-sm">Manage customer pickups</p>
            </div>
          </Link>

          <Link
            to="/tracking"
            className="flex items-center p-4 bg-gray-700 rounded-lg hover:bg-gray-600 transition-colors"
          >
            <div className="p-3 bg-green-600 rounded-lg mr-4">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
              </svg>
            </div>
            <div>
              <p className="text-white font-medium">Track Shipments</p>
              <p className="text-gray-400 text-sm">Monitor in-transit orders</p>
            </div>
          </Link>
        </div>
      </div>

      {/* Upcoming Events */}
      {upcomingEvents.length > 0 && (
        <div className="bg-gray-800 rounded-xl p-6 border-l-4 border-l-blue-500">
          <h2 className="text-lg font-semibold text-white mb-4">Upcoming Events</h2>
          <div className="space-y-3">
            {upcomingEvents.map(event => (
              <div key={event.id} className="bg-gray-700 p-3 rounded-lg flex items-start gap-3">
                <div
                  className="w-2 h-2 rounded-full mt-2 flex-shrink-0"
                  style={{ backgroundColor: getEventColor(event.eventType, event.color) }}
                />
                <div className="flex-1 min-w-0">
                  <div className="text-white font-medium">{event.title}</div>
                  <div className="text-gray-400 text-sm">
                    {formatEventDate(event.startTime, event.allDay)}
                    {event.location && ` • ${event.location}`}
                  </div>
                  {event.description && (
                    <div className="text-gray-500 text-sm mt-1 truncate">{event.description}</div>
                  )}
                </div>
                <span className="text-xs px-2 py-1 rounded bg-gray-600 text-gray-300 capitalize flex-shrink-0">
                  {event.eventType.replace('_', ' ')}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
